import { InfoCircleOutlined } from "@ant-design/icons";
import { useAppSelector } from "@/store/hooks";
import { Modal, message, Form, Input, Radio } from "antd";
import { useState, useEffect, useCallback } from "react";

import {
  saveOrganizationTemplate,
  publishCommunityTemplate,
} from "@/api/weave-x";
import {
  selectUser,
  selectProfileData,
} from "@/store/features/userinfo/userinfoSlice";

interface SaveTemplateModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function SaveTemplateModal({ isOpen, onClose }: SaveTemplateModalProps) {
  const [form] = Form.useForm();
  const [submitLoading, setSubmitLoading] = useState(false);

  const clientId = useAppSelector(selectUser)?.client_id;
  const userId = useAppSelector(selectUser)?.id;
  const user = useAppSelector(selectUser);
  const profileData = useAppSelector(selectProfileData);

  // Feature gating logic for Community Templates
  const shouldShowCommunityTemplates = useCallback(() => {
    const isProduction = import.meta.env.PROD;
    const allowedClientIds = [
      "client-28a46738-6f41-4fa2-b36f-5a220abcd839",
      "client-37f1aabd-4c19-458d-a8cf-a7591c573444",
    ];

    // In staging environment, show community templates to all users
    if (!isProduction) {
      return true;
    }

    // In production environment, only show to specific clients
    return allowedClientIds.includes(clientId);
  }, [clientId]);

  const [shareType, setShareType] = useState<"organization" | "community">(
    "organization",
  );

  // Reset shareType to organization when community templates are not available
  useEffect(() => {
    if (!shouldShowCommunityTemplates() && shareType === "community") {
      setShareType("organization");
    }
  }, [shareType, shouldShowCommunityTemplates]);

  const { visualization } = useAppSelector((state) => state.canvas);

  const templateName = Form.useWatch("templateName", form);
  const templateDescription = Form.useWatch("templateDescription", form);

  const handleSaveTemplate = async () => {
    try {
      setSubmitLoading(true);
      await form.validateFields();

      if (shareType === "organization") {
        await saveOrganizationTemplate({
          clientId,
          templateName,
          templateDescription,
          templateSourceCode: visualization.sourceCode,
          createdBy: userId,
        });
        message.success("Template saved successfully");
      } else if (shareType === "community") {
        const values = form.getFieldsValue();

        await publishCommunityTemplate({
          templateName: values.templateName,
          templateDescription: values.templateDescription,
          templateSourceCode: visualization.sourceCode,
          publisherInfo: {
            riaName: profileData?.name || "",
            clientId: user?.client_id || "",
            advisorId: user?.id || "",
            advisorName: user?.name || "",
            advisorEmail: user?.email || "",
          },
        });
        message.success("Template made public successfully");
      }

      form.resetFields();
      onClose();
    } catch (error: unknown) {
      console.error("Error saving template:", error);

      const isAxiosError = (
        err: unknown,
      ): err is { response: { status: number; data: { message: string } } } => {
        return (
          err !== null &&
          typeof err === "object" &&
          "response" in err &&
          err.response !== null &&
          typeof err.response === "object" &&
          "status" in err.response
        );
      };

      // Handle 409 Conflict specifically for duplicate community templates
      if (
        isAxiosError(error) &&
        error.response.status === 409 &&
        shareType === "community"
      ) {
        message.error(
          "This template has already been shared by someone else. Please modify your template content and try again.",
        );
      } else if (
        isAxiosError(error) &&
        error.response.data &&
        typeof error.response.data === "object" &&
        "message" in error.response.data &&
        typeof error.response.data.message === "string"
      ) {
        message.error(
          `Failed to save template: ${error.response.data.message}`,
        );
      } else {
        message.error(`Failed to save template: ${error}`);
      }
    } finally {
      setSubmitLoading(false);
    }
  };

  const [formValid, setFormValid] = useState(false);

  useEffect(() => {
    const basicFieldsValid =
      templateName &&
      templateName.trim() !== "" &&
      templateDescription &&
      templateDescription.trim() !== "";

    const fieldsErrors = form.getFieldsError();
    const hasErrors = fieldsErrors.some(({ errors }) => errors.length > 0);

    setFormValid(basicFieldsValid && !hasErrors);
  }, [templateName, templateDescription, form]);

  useEffect(() => {
    if (isOpen) {
      form.resetFields();
      setFormValid(false);
      setShareType("organization");
    }
  }, [isOpen, form]);

  return (
    <Modal
      title="Save as Template"
      open={isOpen}
      onCancel={onClose}
      onOk={handleSaveTemplate}
      okText={shareType === "organization" ? "Save" : "Publish"}
      cancelText="Cancel"
      confirmLoading={submitLoading}
      okButtonProps={{
        disabled: !formValid,
      }}
      destroyOnClose={true}
    >
      <div className="save-template-content">
        {shouldShowCommunityTemplates() ? (
          <div style={{ marginBottom: 16 }}>
            <Radio.Group
              value={shareType}
              onChange={(e) => setShareType(e.target.value)}
            >
              <Radio value="organization">
                <strong>Save to Organization</strong>
              </Radio>
              <Radio value="community">
                <strong>Make it Public</strong>
              </Radio>
            </Radio.Group>
          </div>
        ) : null}

        <div className="save-template-content-info">
          <InfoCircleOutlined />
          <span>
            {shareType === "organization"
              ? "Saving your canvas as a template will allow you to reuse it later. You can access your templates in planning templates. Note: Saved templates will be shared with your organization."
              : "Making your template public will make it available to all users. Public templates help others discover and use your work."}
          </span>
        </div>
        <div className="save-template-content-form">
          <Form form={form} name="save-template-form" layout="vertical">
            <Form.Item
              name="templateName"
              label="Template Name"
              rules={[
                {
                  required: true,
                  message: "Please enter a name for your template",
                },
                {
                  whitespace: true,
                  message: "Template name cannot be empty",
                },
              ]}
            >
              <Input placeholder="Enter a name for your template" />
            </Form.Item>
            <Form.Item
              name="templateDescription"
              label="Template Description"
              rules={[
                {
                  required: true,
                  message: "Please enter a description for your template",
                },
                {
                  whitespace: true,
                  message: "Template description cannot be empty",
                },
              ]}
            >
              <Input.TextArea placeholder="Enter a description for your template" />
            </Form.Item>
          </Form>
        </div>
      </div>
    </Modal>
  );
}
